import React from "react";

import { Organization, User } from "@pollyslack/hub/src/types";

import {
  BotColumns,
  UserColumns,
  UsersTable,
} from "../../../components/UsersTable";

import { searchUsersAdvanced } from "../../../services/api/User";
import { DocumentTabNav } from "../../../components/DocumentTabNav";
import {
  DocumentSearch,
  DocumentSearchParams,
} from "../../../components/DocumentSearch";
import { useDebounce } from "use-debounce";
import Loader from "../../../common/Loader";

type UsersTabProps = {
  organization: Organization;
  reload: () => Promise<void>;
};

export const UsersTab: React.FunctionComponent<UsersTabProps> = (
  props: UsersTabProps,
) => {
  const [users, setUsers] = React.useState<User[]>([]);
  const [tab, setTab] = React.useState<
    "all" | "pollyAdmins" | "slackAdmins" | "bots"
  >("all");

  const [nameSearch, setNameSearch] = React.useState("");
  const [idSearch, setIdSearch] = React.useState("");
  const [showDeleted, setShowDeleted] = React.useState(false);

  const [debouncedNameSearch] = useDebounce(nameSearch, 500);
  const [debouncedIdSearch] = useDebounce(idSearch, 500);
  const [debouncedShowDeleted] = useDebounce(showDeleted, 500);
  const [isLoading, setIsLoading] = React.useState(false);

  const tabs = [
    {
      id: "all",
      label: "All Users",
      onClick: () => setTab("all"),
    },
    {
      id: "pollyAdmins",
      label: "Polly Admins",
      onClick: () => setTab("pollyAdmins"),
    },
    {
      id: "slackAdmins",
      label: "Slack Admins",
      onClick: () => setTab("slackAdmins"),
    },
    {
      id: "bots",
      label: "Bots",
      onClick: () => setTab("bots"),
    },
  ];

  React.useEffect(() => {
    let cancelled = false;
    async function loadUsers() {
      setIsLoading(true);
      const fetchedUsers = await searchUsersAdvanced({
        organizationId: props.organization._id,
        namePattern: debouncedNameSearch,
        idSearch: debouncedIdSearch,
        onlyPollyAdmins: tab === "pollyAdmins",
        onlySlackAdmins: tab === "slackAdmins",
        onlyBots: tab === "bots",
        showDeleted: debouncedShowDeleted,
      });
      if (!cancelled) {
        setUsers(fetchedUsers);
      }
      setIsLoading(false);
    }
    loadUsers();
    return function cleanup() {
      cancelled = true;
    };
  }, [
    props.organization._id,
    tab,
    debouncedNameSearch,
    debouncedIdSearch,
    debouncedShowDeleted,
  ]);

  let displayedUsers: User[] = users;
  if (tab === "bots") {
    const sortKey = (user: User): string =>
      `${
        (user.profile.firstName ? user.profile.firstName + " " : "") +
          (user.profile.lastName ?? "") || user.profile.name
      }`.toLowerCase();
    displayedUsers = [...users].sort((u1, u2) =>
      sortKey(u1) > sortKey(u2) ? 1 : -1,
    );
  }

  const columnOverwrites: UserColumns & BotColumns =
    tab === "bots"
      ? {
          colEmail: false,
          colTitle: false,
          colPollyAdmin: false,
          colSlackRole: false,
          colInstalledAt: true,
        }
      : {};
  return (
    <>
      <div className="row ml-0">
        <div className="ml-4" style={{ width: "100%", marginBottom: "1rem" }}>
          <div
            style={{
              width: "100%",
              minWidth: "250px",
              marginBottom: "0.75rem",
            }}
          >
            <DocumentSearch
              typeName={"User"}
              placeholder={"Name, Title, or Email"}
              numFound={displayedUsers.length}
              onSubmit={(searchParams: DocumentSearchParams) => {
                setNameSearch(searchParams.search);
                setShowDeleted(searchParams.showDeleted);
              }}
              loading={isLoading}
            />
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className="input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text">
                    <i className="fas fa-search" />
                  </span>
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Mongo ID, Slack User ID, or Platform ID"
                  value={idSearch}
                  onChange={(e) => setIdSearch(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
        <DocumentTabNav initialTab={tab} tabs={tabs} />
      </div>
      {isLoading ? (
        <Loader />
      ) : (
        <UsersTable
          users={displayedUsers}
          colOrg={false}
          {...columnOverwrites}
        />
      )}
    </>
  );
};
